import { UPDATE_GAMEPLAY_SETTINGS, updateGameplaySettings } from '@/store/modules/pick-em/actions';
import userAdapter, { bonusWalletAccountAdapter } from '@/store/modules/user/adapters/user';

import {
  BonusBalanceUpdateResponse,
  GameplaySettings,
  IdentificationStatusChangeResponse,
  RegistrationSourceResponse,
  User,
  UserBonusWalletResponse,
  UserCosmeticUpdateResponse,
  UserResponse,
  WalletBalanceUpdateResponse,
} from '@/interfaces/user';
import { toCamel } from '@/utilities/helpers/to-camel';

import {
  SET_USER,
  SET_USER_BONUS_WALLET,
  SET_USER_NEW_REGISTRATION,
  SET_USER_REGISTRATION_SOURCE,
  setUser,
  setUserBonusWallet,
  setUserNewRegistration,
  setUserRegistrationSource,
  UPDATE_NOT_NEW_USER,
  UPDATE_USER,
  UPDATE_USER_BONUS_CASH,
  UPDATE_USER_COSMETIC,
  UPDATE_USER_ID_STATUS,
  UPDATE_USER_WALLET,
  UPDATE_VERIFY_USER,
  updateNotNewUser,
  updateUser,
  updateUserBonusCash,
  updateUserCosmetic,
  updateUserIdStatus,
  updateUserWallet,
  updateVerifyUser,
} from '../actions';

type State = User;

export type UserActions = ReturnType<
  | typeof setUser
  | typeof setUserBonusWallet
  | typeof setUserNewRegistration
  | typeof setUserRegistrationSource
  | typeof updateGameplaySettings
  | typeof updateUserIdStatus
  | typeof updateVerifyUser
  | typeof updateNotNewUser
  | typeof updateUser
  | typeof updateUserWallet
  | typeof updateUserBonusCash
  | typeof updateUserCosmetic
>;

export interface UserState {
  user: User;
}

const initialUserState: User = {
  id: null,
  appDownloadEligible: false,
  badges: [],
  bonusCash: null,
  bonusWallet: {
    bonusWalletAccounts: [],
    nonExpiringBonusBalance: null,
  },
  email: null,
  gameplaySettings: null,
  hasAcceptedLatestProductPolicies: null,
  hasMigratedFromSportsbook: null,
  hasPhone: null,
  identificationStatus: null,
  newRegistration: null,
  permissions: null,
  preRegEligible: false,
  profile: null,
  profileColor: null,
  profileIconUrl: null,
  product: null,
  registrationSource: null,
  rgContentfulId: null,
  roles: [],
  state: null,
  stateConfig: {
    pickEm: {
      maximumSelectionSize: Infinity,
      minimumSelectionSize: 2, // this is with almost certainty the lowest this value will be
      payoutModifier: false,
      pickemPoolsEnabled: null,
      pickemPoolsFreeEntry: false,
      pickemPoolsVisible: null,
      visibleSports: null,
      visibleStats: null,
    },
    sportsbook: {
      disableWebApp: false,
      enabled: false,
      wagerServiceUrl: '',
    },
  },
  stateConfigId: null,
  username: null,
  wallet: {
    id: null,
    balance: null,
    version: null,
  },
};

const set = (state: State, user: User | undefined): User => {
  if (!user) {
    return { ...initialUserState };
  }

  const newState = {
    ...user,
  };

  // This is a bit counter-intuitive because `set` implies that whatever
  // `user` argument is passed in will be set as the new state. However, we
  // have an odd scenario with `stateConfig` where that property is not
  // part of the API `User` object/response. Instead it comes from a different
  // endpoint and is attached to the user object in redux state.
  // When we fetch the anonymous user, it doesn't have a `stateConfig` property
  // and a call to the `stateConfig` endpoint may fail for anonymous user.
  // Or, the anonymous user call may succeed _after_ the `stateConfig` call.
  // So, we now check if the incoming `user` object has a `stateConfig` property
  // and if it doesn't, we try to set the `stateConfig` property from the existing state.
  // A bit hackish, but we can revisit if it becomes a problem.
  if (state.stateConfig && !user.stateConfig) {
    newState.stateConfig = state.stateConfig;
  }

  return newState;
};

const updateVerify = (state: User, { data }: { data: UserResponse }): User => {
  const user = userAdapter({ user: data.user });
  return {
    ...state,
    ...user,
  };
};

const updateIdStatus = (state: User, data: IdentificationStatusChangeResponse): User => {
  if (state.id !== data.id) return state;
  return {
    ...state,
    identificationStatus: toCamel(data.identification_status) as User['identificationStatus'],
  };
};

const updateWallet = (state: User, data: WalletBalanceUpdateResponse): User => {
  if (state.id !== data.id) return state;
  return {
    ...state,
    wallet: {
      ...state.wallet,
      balance: data.balance,
    },
  };
};

const updateBonusCash = (state: User, data: BonusBalanceUpdateResponse): User => {
  if (state.id !== data.id) return state;

  return {
    ...state,
    bonusCash: data.bonus_cash,
  };
};

const setBonusWallet = (state: User, data: UserBonusWalletResponse): User => {
  const bonusWalletAccounts = data.bonus_wallet_accounts.map(bonusWalletAccountAdapter);

  return {
    ...state,
    bonusWallet: {
      bonusWalletAccounts,
      nonExpiringBonusBalance: data.non_expiring_bonus_balance,
    },
  };
};

const updateCosmetic = (state: User, data: UserCosmeticUpdateResponse): User => {
  const updatedUserResponse = data.user;

  return {
    ...state,
    profileColor: updatedUserResponse.profile_color,
    profileIconUrl: updatedUserResponse.profile_icon_url,
  };
};

const setNewRegistration = (state: User) => {
  return {
    ...state,
    newRegistration: true,
  };
};

const setNotNewRegistration = (state: User): User => ({
  ...state,
  newRegistration: null,
});

const setRegistrationSource = (state: User, payload: RegistrationSourceResponse): User => ({
  ...state,
  registrationSource: payload.registration_source,
});

const updateUserGameplaySettings = (state: User, gameplaySettings: GameplaySettings): User => ({
  ...state,
  gameplaySettings,
});

const updateUserData = (state: User, payload: Partial<User>): User => {
  const newState = {
    ...state,
    ...payload,
  };

  return newState;
};

export const userReducer = (
  state: State = initialUserState,
  action: UserActions = {} as UserActions
): State => {
  switch (action.type) {
    case SET_USER:
      return set(state, action.payload);
    case SET_USER_BONUS_WALLET:
      return setBonusWallet(state, action.payload);
    case SET_USER_NEW_REGISTRATION:
      return setNewRegistration(state);
    case SET_USER_REGISTRATION_SOURCE:
      return setRegistrationSource(state, action.payload);
    case UPDATE_USER:
      return updateUserData(state, action.payload);
    case UPDATE_GAMEPLAY_SETTINGS:
      return updateUserGameplaySettings(state, action.payload);
    case UPDATE_VERIFY_USER:
      return updateVerify(state, action.payload);
    case UPDATE_NOT_NEW_USER:
      return setNotNewRegistration(state);
    case UPDATE_USER_ID_STATUS:
      return updateIdStatus(state, action.payload);
    case UPDATE_USER_WALLET:
      return updateWallet(state, action.payload);
    case UPDATE_USER_BONUS_CASH:
      return updateBonusCash(state, action.payload);
    case UPDATE_USER_COSMETIC:
      return updateCosmetic(state, action.payload);
    default:
      return state;
  }
};
